import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../api";
import { Button } from "../common";
import SectionShell, { FieldGroup, FieldGroups } from "./SectionShell";

/**
 * The daily housekeeping job, and a manual trigger for it.
 *
 * No save bar: there is nothing to save here, only something to run. The result
 * is rendered as counted outcomes rather than a "done" toast, because the whole
 * reason to trigger this by hand is to find out what it actually did.
 */
export default function AutomationSettings() {
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setRunning(true);
    setError("");
    try {
      const r = await api.post("/settings/housekeeping/run");
      setResult(r.data);
    } catch (e) {
      console.error("Housekeeping run failed:", e);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const OUTCOMES = result?.ran ? [
    { labelKey: "settings.housekeepingFollowUps",     value: result.follow_up_tasks_created },
    { labelKey: "settings.housekeepingStale",         value: result.deals_stale_flag_changed },
    { labelKey: "settings.housekeepingNotifications", value: result.users_notifications_synced },
  ] : [];

  return (
    <SectionShell
      icon={RefreshCw} tone="amber"
      title={t("settings.housekeeping")} desc={t("settings.housekeepingDesc")}
      error={error}>

      <FieldGroups>
        <FieldGroup label={t("settings.housekeepingManual")} hint={t("settings.housekeepingManualHint")}>
          <div>
            <Button onClick={run} disabled={running} data-testid="run-housekeeping-btn">
              {running
                ? <><Loader2 size={16} className="animate-spin" /> {t("settings.housekeepingRunning")}</>
                : <><RefreshCw size={16} /> {t("settings.housekeepingRun")}</>}
            </Button>
          </div>

          {result && (
            <div data-testid="housekeeping-result" className="pt-1">
              {result.ran ? (
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {OUTCOMES.map(({ labelKey, value }) => (
                    <div key={labelKey} className="rounded-sm border border-border bg-surface px-3.5 py-3">
                      <dt className="text-xs uppercase tracking-[0.15em] text-muted">{t(labelKey)}</dt>
                      <dd className="font-display text-2xl font-bold tabular-nums mt-1">{value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                // The job takes a cross-worker advisory lock, so "skipped" is a
                // normal outcome (the scheduled run had it), not a failure.
                <p className="text-sm text-muted">{t("settings.housekeepingSkipped")}</p>
              )}
            </div>
          )}
        </FieldGroup>
      </FieldGroups>
    </SectionShell>
  );
}
